import type { Portfolio } from "@/types/Portfolio";
import type { CategoryQuery } from "@/widget-components/newsquawk-headlines/models/CategoryQuery";
import type { WidgetFilterData } from "@/utils/DashboardLayouts";
import { effectiveOrder, replaceKey } from "@/shared/utils/highlightOrder";

/**
 * Which record a list from the builder is saved as.
 *
 * The user builds one thing — a watchlist — and never chooses. Typed tickers,
 * companies and topics alone are a plain watchlist. Anything a watchlist
 * record can't hold makes it a combined watchlist: a tag kept from a typed
 * term, a tag or list picked from the library, or a keyword expression. The
 * typed terms then live in a hidden watchlist inside it (component_only),
 * which is how the desk already stores index lists — and pref-storage has no
 * field for tags on a watchlist at all.
 *
 * A combined list stays combined when edited, even if its extras go: it may
 * be selected in dashboards as a combined watchlist, and turning it back would
 * be one more identity change to follow.
 */
export type ListStorage = "watchlist" | "combined";

export interface ListParts {
  hasTypedTerms: boolean;
  /** Tags kept from typed terms (the match review's survivors). */
  keptTags: number;
  /** Tags picked from the library. */
  pickedTags: number;
  /** Other watchlists this one combines with. */
  linkedLists: number;
  tagWatchlists: number;
  hasKeywordExpression: boolean;
}

export function storageFor(
  parts: ListParts,
  editing?: ListStorage | "tag"
): ListStorage {
  // A tag watchlist is no longer something to build: saving one makes it a
  // combined watchlist, whatever it holds.
  if (editing === "combined" || editing === "tag") return "combined";
  const extras =
    parts.keptTags +
    parts.pickedTags +
    parts.linkedLists +
    parts.tagWatchlists +
    (parts.hasKeywordExpression ? 1 : 0);
  return extras === 0 ? "watchlist" : "combined";
}

/**
 * The watchlist a combined list keeps its typed terms in, if it has one.
 *
 * Only a hidden one with the combined list's own owner: "Build on" copies a
 * Newsquawk combination's linked lists, which can include the desk's own
 * hidden ingredient, and that is the library's to edit, not the user's.
 */
export function typedTermsWatchlist(query: CategoryQuery): Portfolio | undefined {
  const owner = query.owner_type ?? "USER";
  const hidden = (query.portfolios ?? []).filter(
    (p) => p.component_only && p.owner_type === owner
  );
  return hidden.length === 1 ? hidden[0] : undefined;
}

/**
 * Whether a combined watchlist includes the tag watchlist.
 *
 * Such a tag watchlist is saved in place rather than converted: converting
 * deletes it, and pref-storage drops the link with it, so the list including
 * it would silently lose its tags.
 */
export function isIncludedTagWatchlist(
  queries: CategoryQuery[] | undefined,
  tagWatchlistId: number
): boolean {
  return (queries ?? []).some((q) => q.tagsWatchlistIds?.includes(tagWatchlistId));
}

/** The dashboard sections each kind of list is selected under. */
const SECTIONS = {
  watchlist: {
    filter: ["ticker-watchlists-filter", "keyword-watchlists-filter"],
    highlight: ["ticker-watchlists-highlight", "keyword-watchlists-highlight"],
  },
  tag: {
    filter: ["tag-watchlists-filter"],
    highlight: ["tag-watchlists-highlight"],
  },
} as const;

/** A list that turns into a combined watchlist when saved in the builder. */
export type ConvertedKind = keyof typeof SECTIONS;

/**
 * Dashboard selections of a list, moved to the combined watchlist it has
 * become.
 *
 * Widgets record a list as a bare id under the section for its kind. A
 * converted watchlist is hidden from the lists those sections are built from,
 * and a converted tag watchlist is deleted; either way, every widget left
 * filtering by it would keep filtering by a row nobody can see or untick, and
 * highlights would silently stop. OR-only combined watchlists filter
 * alongside other lists, so the move is exact.
 *
 * The tag-watchlist sections also hold bare tag ids, and an id there is read
 * as a tag watchlist first (NewsquawkHeadlines), so the id being moved was
 * that tag watchlist wherever it appeared.
 *
 * A watchlist keeps its place in each widget's highlight order: the combined
 * list takes its key there rather than joining the bottom.
 *
 * Returns the same object when nothing referred to the list, so callers can
 * skip the write.
 */
export function moveSelectionsToCombined(
  widgetFilters: Record<string, WidgetFilterData> | undefined,
  kind: ConvertedKind,
  listId: number,
  combinedId: number
): Record<string, WidgetFilterData> | undefined {
  if (!widgetFilters) return widgetFilters;
  const from = String(listId);
  const to = String(combinedId);
  let changed = false;
  const next: Record<string, WidgetFilterData> = {};

  for (const [widgetId, data] of Object.entries(widgetFilters)) {
    let widget = data;
    for (const verb of ["filter", "highlight"] as const) {
      const selections = widget[verb];
      if (!selections) continue;
      const holding = SECTIONS[kind][verb].filter((key) =>
        selections[key]?.includes(from)
      );
      if (holding.length === 0) continue;

      const moved: Record<string, string[]> = { ...selections };
      for (const key of holding) {
        moved[key] = moved[key].filter((id) => id !== from);
      }
      const combinedKey = `category-${verb}`;
      const current = moved[combinedKey] ?? [];
      moved[combinedKey] = current.includes(to) ? current : [...current, to];

      widget = { ...widget, [verb]: moved };
      if (verb === "highlight" && kind === "watchlist") {
        const order = replaceKey(
          effectiveOrder(data.highlight, data.highlightOrder),
          `watchlist:${listId}`,
          `combined:${combinedId}`
        );
        if (order) widget = { ...widget, highlightOrder: order };
      }
      changed = true;
    }
    next[widgetId] = widget;
  }
  return changed ? next : widgetFilters;
}
